import { useCallback, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import Decimal from 'decimal.js'

import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositions'
import { PositionConfigInfo, round, CoinInfo, SUI, USDC } from '@kunalabs-io/kai'
import { Dialog } from 'components/Dialog/Dialog'
import { Typography } from 'components/Typography/Typography'
import { Button } from 'components/Button/Button'
import { useIsMobile } from 'utils/useIsMobile'
import Checkbox from 'components/Checkbox/Checkbox'
import { ConvertRewardsToMenu } from './ConvertRewardsSelect'
import { vaultDisplayInfoList } from 'utils/vaultDisplayInfo'
import { PendingFeesRewards } from './PendingFeesRewards'
import { Spinner } from 'components/Spinner/Spinner'
import { useRewardsFeesTotalValue } from './hooks/useRewardsFeesTotalValue'
import { ClaimInfoSubmit } from './types'
import { DevInspectLpUnclaimedRewardsResult } from '@kunalabs-io/kai'

const ButtonsContainer = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  & > div {
    flex-basis: 50%;
  }
`

const CheckboxContainer = styled.div`
  display: inline-flex;
  margin-top: 24px;
  margin-bottom: 16px;
  cursor: pointer;
`

const SpinnerContainer = styled.div`
  margin-top: 40px;
  width: 100%;
  display: flex;
  justify-content: center;
`

const ErrorLabel = styled(Typography)`
  color: ${p => p.theme.colors.text.danger};
`

interface Props {
  isLoadingClaim: boolean
  isLoadingCompound: boolean
  positionInfo: PositionInfo
  isOpen: boolean
  onClose: () => void
  configInfo?: PositionConfigInfo<string, string, TypeArgument>
  isReversedPair: boolean
  onClaim: (args: ClaimInfoSubmit) => Promise<void>
  onCompound: () => Promise<void>
  rewardsFeesInfos: DevInspectLpUnclaimedRewardsResult | null
  isLoadingRewardsFees: boolean
  rewardsFeesInfoError: Error | null
}

export const convertToTokenList = vaultDisplayInfoList.map(v => v.vault.T)

export const ClaimDialog = ({
  isOpen,
  onClose,
  configInfo,
  isReversedPair,
  positionInfo,
  onClaim,
  onCompound,
  isLoadingClaim,
  isLoadingCompound,
  rewardsFeesInfos,
  rewardsFeesInfoError,
  isLoadingRewardsFees,
}: Props) => {
  // Checkbox state
  const [convertRewards, setConvertRewards] = useState(false)
  // Select component state, set initial value to SUI
  const [convertRewardsTo, setConvertRewardsTo] = useState(convertToTokenList.find(v => v.typeName === SUI.typeName)!)

  const { isLoading: isLoadingTotalValue, totalSum } = useRewardsFeesTotalValue(
    positionInfo,
    rewardsFeesInfos,
    convertRewardsTo
  )

  const { isLoading: isLoadingTotalValueUsd, totalSum: totalSumUsd } = useRewardsFeesTotalValue(
    positionInfo,
    rewardsFeesInfos,
    USDC
  )

  const isMobile = useIsMobile()

  const theme = useTheme()

  const handleConvertRewardsChange = useCallback(() => {
    setConvertRewards(!convertRewards)
  }, [convertRewards])

  const handleConvertRewardsToClick = useCallback((o: CoinInfo<PhantomTypeArgument>) => {
    setConvertRewardsTo(o)
  }, [])

  if (rewardsFeesInfoError) {
    return (
      <Dialog title="Claim fees" isOpen={isOpen} onClose={onClose}>
        <ErrorLabel variant="body">Something went wrong while loading fees and rewards</ErrorLabel>
      </Dialog>
    )
  }

  if (isLoadingRewardsFees || !rewardsFeesInfos) {
    return (
      <Dialog title="Claim fees" isOpen={isOpen} onClose={onClose}>
        <SpinnerContainer>
          <Spinner style={{ width: 36, height: 36 }} color={theme.colors.text.primary} />
        </SpinnerContainer>
      </Dialog>
    )
  }

  return (
    <Dialog title="Claim fees" isOpen={isOpen} onClose={onClose}>
      <div style={{ height: 24 }} />
      <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="semiBold">
        Breakdown
      </Typography>
      <PendingFeesRewards isReversedPair={isReversedPair} configInfo={configInfo} rewards={rewardsFeesInfos} />
      <CheckboxContainer onClick={handleConvertRewardsChange}>
        <Checkbox checked={convertRewards} />
        <Typography variant="body" color="secondary" style={{ marginLeft: 6 }}>
          Convert rewards to:
        </Typography>
      </CheckboxContainer>
      {convertRewardsTo && (
        <ConvertRewardsToMenu
          isLoading={isLoadingTotalValue || isLoadingTotalValueUsd}
          disabled={!convertRewards}
          options={convertToTokenList}
          onOptionClick={handleConvertRewardsToClick}
          activeOption={convertRewardsTo}
          rewardsValue={totalSum}
          rewardsValueDollars={totalSumUsd}
        />
      )}
      <ButtonsContainer>
        <Button
          outlined
          disabled={isLoadingCompound}
          onClick={() => onClaim({ convertRewardsTo: convertRewards ? convertRewardsTo : undefined })}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 49,
          }}
        >
          {isLoadingClaim ? (
            <Spinner style={{ marginTop: 0, marginLeft: 'inherit' }} color={theme.colors.text.primary} />
          ) : (
            'Claim'
          )}
        </Button>
        <Button
          disabled={isLoadingClaim}
          onClick={onCompound}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 49,
          }}
        >
          {isLoadingCompound ? <Spinner style={{ marginTop: 0, marginLeft: 'inherit' }} /> : 'Compound'}
        </Button>
      </ButtonsContainer>
    </Dialog>
  )
}
